import React from 'react';
import { List, Button, Collapse, Tooltip } from 'antd';
import { Clock, Check, Eye, X, Trash2, Mail, Store, UserRound, Building2, CalendarClock } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { StatusTag, IconAction, IconActions } from '../../common';
import { getSeverityConfig, getTypeConfig } from './notificationConfig';

dayjs.extend(relativeTime);
dayjs.locale('es');

/** Inline link-style button for the related client / store. */
const MetaLink = ({ icon, children, onClick }) => (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline">
        <span className="text-surface-400 flex">{icon}</span>
        {children}
    </button>
);

const MetaText = ({ icon, children }) => (
    <span className="inline-flex items-center gap-1.5 text-sm text-surface-600">
        <span className="text-surface-400 flex">{icon}</span>
        {children}
    </span>
);

export default function NotificationList({ notifications, markAsRead, dismissNotification, deleteNotification, viewStoreCheck, viewClient, viewStore }) {
    return (
        <List
            className="[&_.ant-list-pagination]:px-5 [&_.ant-list-pagination]:pb-4"
            itemLayout="vertical"
            dataSource={notifications}
            pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => (
                    <span className="text-surface-500">
                        Total <strong className="tabular-nums">{total}</strong> notificaciones
                    </span>
                )
            }}
            renderItem={(notification) => {
                const severityConfig = getSeverityConfig(notification.severity);
                const typeConfig = getTypeConfig(notification.type);
                const isUnread = !notification.read;

                return (
                    <List.Item
                        key={notification._id}
                        className={`relative !px-5 !py-4 transition-colors ${isUnread ? 'bg-primary-50/40' : 'hover:bg-surface-25'}`}>
                        {isUnread && <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${severityConfig.accent}`} />}

                        <div className="flex flex-col gap-3">
                            {/* Tags, time and actions */}
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <StatusTag tone={typeConfig.tone} icon={typeConfig.icon}>
                                        {typeConfig.text}
                                    </StatusTag>
                                    <StatusTag tone={severityConfig.tone} icon={severityConfig.icon}>
                                        {severityConfig.text}
                                    </StatusTag>
                                    {notification.emailSent && (
                                        <StatusTag tone="success" icon={<Mail />}>
                                            Email enviado
                                        </StatusTag>
                                    )}
                                    {isUnread && (
                                        <StatusTag tone="brand" dot>
                                            Nueva
                                        </StatusTag>
                                    )}
                                    <span className="ml-1 inline-flex items-center gap-1 text-xs text-surface-400">
                                        <Clock />
                                        {dayjs(notification.createdAt).fromNow()}
                                    </span>
                                </div>

                                <div className="flex flex-wrap items-center gap-1">
                                    {isUnread && (
                                        <Tooltip title="Marcar como leída">
                                            <Button type="text" size="small" icon={<Check />} onClick={() => markAsRead(notification._id)}>
                                                Marcar leída
                                            </Button>
                                        </Tooltip>
                                    )}
                                    {notification.storeCheckId && (
                                        <Button type="text" size="small" icon={<Eye />} onClick={() => viewStoreCheck(notification.storeCheckId)}>
                                            Ver Auditoría
                                        </Button>
                                    )}
                                    <IconActions>
                                        <IconAction icon={<X />} label="Descartar" onClick={() => dismissNotification(notification._id)} />
                                        <IconAction
                                            icon={<Trash2 />}
                                            label="Eliminar"
                                            danger
                                            onClick={() => deleteNotification(notification._id)}
                                            confirm={{
                                                title: 'Eliminar notificación',
                                                description: '¿Está seguro que desea eliminar esta notificación?',
                                                okText: 'Sí',
                                                cancelText: 'No'
                                            }}
                                        />
                                    </IconActions>
                                </div>
                            </div>

                            {/* Title and message */}
                            <div>
                                <h3 className={`m-0 text-[15px] text-surface-900 ${isUnread ? 'font-semibold' : 'font-medium'}`}>{notification.title}</h3>
                                <p className="m-0 mt-1 text-sm text-surface-600">{notification.message}</p>
                            </div>

                            {/* Metadata Section */}
                            {(notification.metadata?.clientName ||
                                notification.metadata?.storeName ||
                                notification.metadata?.userName ||
                                notification.metadata?.auditDate) && (
                                <div className="flex flex-wrap gap-x-5 gap-y-2">
                                    {notification.metadata?.clientName && (
                                        <MetaLink icon={<Building2 />} onClick={() => viewClient(notification.clientId)}>
                                            {notification.metadata.clientName}
                                        </MetaLink>
                                    )}
                                    {notification.metadata?.storeName && (
                                        <MetaLink icon={<Store />} onClick={() => viewStore(notification.storeId)}>
                                            {notification.metadata.storeName}
                                        </MetaLink>
                                    )}
                                    {notification.metadata?.userName && <MetaText icon={<UserRound />}>{notification.metadata.userName}</MetaText>}
                                    {notification.metadata?.auditDate && (
                                        <MetaText icon={<CalendarClock />}>
                                            <span className="tabular-nums">{dayjs(notification.metadata.auditDate).format('DD/MM/YYYY HH:mm')}</span>
                                        </MetaText>
                                    )}
                                </div>
                            )}

                            {/* Affected Products */}
                            {notification.affectedProducts && notification.affectedProducts.length > 0 && (
                                <Collapse
                                    ghost
                                    size="small"
                                    className="rounded-lg border border-surface-200 bg-white"
                                    items={[
                                        {
                                            key: '1',
                                            label: (
                                                <span className="inline-flex items-center gap-2 text-sm font-medium text-surface-700">
                                                    Ver productos afectados
                                                    <StatusTag tone="danger" className="tabular-nums">
                                                        {notification.affectedProducts.length}
                                                    </StatusTag>
                                                </span>
                                            ),
                                            children: (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {notification.affectedProducts.map((product, idx) => (
                                                        <StatusTag key={idx} tone="danger">
                                                            {product.productName}
                                                            {product.category && <span className="opacity-70">({product.category})</span>}
                                                        </StatusTag>
                                                    ))}
                                                </div>
                                            )
                                        }
                                    ]}
                                />
                            )}

                            {/* Email Recipients */}
                            {notification.emailSentTo && notification.emailSentTo.length > 0 && (
                                <div className="flex items-center gap-2 border-t border-surface-100 pt-2 text-xs text-surface-500">
                                    <Mail className="shrink-0 text-surface-400" />
                                    <span>Enviado a: {notification.emailSentTo.join(', ')}</span>
                                </div>
                            )}
                        </div>
                    </List.Item>
                );
            }}
        />
    );
}
